import { Directive, forwardRef, HostBinding, inject, Input, OnDestroy } from '@angular/core';

import { TranslationService } from 'app/core/internationalization/translation.service';
import { KH_A11Y_TARGET } from '../constants/a11y-target.token';
import { A11yBooleanInput } from '../interfaces/a11y.interfaces';
import { normalizeAriaValue, toBoolean } from '../utils/a11y.util';

import { A11yCarouselContext, A11yCarouselDotContext } from './a11y-carousel.context';
import { A11yLabelDirective } from './a11y-label.directive';

@Directive({
  selector: '[khA11yCarouselDot]',
  providers: [{ provide: KH_A11Y_TARGET, useExisting: forwardRef(() => A11yCarouselDotDirective) }]
})
export class A11yCarouselDotDirective extends A11yLabelDirective implements A11yCarouselDotContext, OnDestroy {
  private readonly _carousel = inject(A11yCarouselContext, { optional: true });
  private readonly _translationService = inject(TranslationService);

  @Input() public khA11yCarouselDotIndex = 0;
  @Input() public khA11yCarouselDotActive: A11yBooleanInput = false;

  public constructor() {
    super();
    this._carousel?.registerDot(this);
  }

  public ngOnDestroy(): void {
    this._carousel?.unregisterDot(this);
  }

  @HostBinding('attr.aria-current')
  public get ariaCurrent(): string | null {
    return toBoolean(this.khA11yCarouselDotActive, true) ? 'true' : null;
  }

  protected override get ariaLabelValue(): string | null {
    return normalizeAriaValue(super.ariaLabelValue) ?? this.defaultDotLabel;
  }

  private get defaultDotLabel(): string {
    const slideNumber = this.khA11yCarouselDotIndex + 1;

    return this._translationService.instant('A11Y_CAROUSEL.GO_TO_SLIDE', { slideNumber }) || `Go to slide ${slideNumber}`;
  }
}
